
















































































































import Vue, { PropType } from 'vue';

// Data
import countries from 'highered-utils/data/countries.json';

// Types and services
import CandidateService, { Candidate } from '~/services/CandidateService';
import CompanyService from '~/services/CompanyService';
import DialogService from '~/services/DialogService';
import { CandidateStudyProgram } from '~/services/CSPService';
import { commonProps } from '~/utils/candidate';
import {
    CandidateProfileScore,
    CandidateProfileScorer,
    CandidateProfileStatus,
} from '~/utils/candidate/CandidateProfileScorer';
import { ArrayItem } from '~/utils/types';
import { ProfileImportResult } from '~/services/types/import';

// Components
import StudyProgramStatusIcon from '~/components/profile/components/StudyProgramStatusIcon.vue';
import Avatar from '~/components/builders/Avatar.vue';
import AttributeIcon, { AttributeIconType } from '../components/AttributeIcon.vue';
import PersonalDetailsModal from '../modals/PersonalDetailsModal.vue';
import Card from './Card.vue';
import { SchoolVerificationState } from '~/services/SchoolService';
import { DateComparerBuilder } from '~/utils/helpers/comparison';
import KebabMenu, { ActionItem } from '~/components/profile/components/KebabMenu.vue';
import LinkedinImportButton from '../components/LinkedinImportButton.vue';

const importBtnId = 'importBtn';
const latestImportBtnId = 'latestImportBtn';

const countryMap = countries.reduce(
    (map, country) => map.set(country.value, country),
    new Map<string, ArrayItem<typeof countries>>()
);

interface Experience {
    schoolId: number;
    school: string;
    program: string;
    workExperience: string;
}

export default Vue.extend({
    name: 'PersonalDetails',
    components: {
        Card,
        Avatar,
        PersonalDetailsModal,
        AttributeIcon,
        StudyProgramStatusIcon,
        KebabMenu,
        LinkedinImportButton,
    },
    props: {
        ...commonProps(),
        score: {
            type: Object as PropType<CandidateProfileScore | null>,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            service: new CandidateService(),
            showModal: false,

            schoolLogo: '',
            discover: this.candidate.profileVisibility === 'public_to_recruiters',
            isDiscoverLoading: false,

            menuItems: [
                { id: importBtnId, title: String(this.$t('profile.import.kebabMenu.importBtn')) },
            ] as ActionItem[],
        };
    },
    computed: {
        fullName(): string {
            return [this.user.firstName, this.user.lastName].filter(x => !!x).join(' ') || 'You';
        },
        nationality(): string | null {
            if (this.candidate.nationalities.length === 0) return null;
            const country = countryMap.get(this.candidate.nationalities[0].nationality || '');
            if (!country) return null;
            return country.label;
        },
        location(): string | null {
            const country = countryMap.get(this.candidate.country || '');
            if (!country) return null;
            return country.label;
        },
        pronoun(): string | null {
            if (!this.candidate.pronoun) return null;
            if (this.$te(`profile.personalDetails.pronounOptions.${this.candidate.pronoun.type}`))
                return this.$t(
                    `profile.personalDetails.pronounOptions.${this.candidate.pronoun.type}`
                ).toString();
            return this.candidate.pronoun.value || null;
        },
        experience(): Experience | null {
            const experience: Partial<Experience> = {};

            // Add Study Program
            const program = this.getLatestStudyProgram();
            if (program && program.studyProgram && program.studyProgram.level) {
                let programString = '';
                if (program.studyProgram.name) {
                    if (program.end) {
                        const end = new Date(program.end);
                        programString = this.$t('profile.personalDetails.studyProgram', {
                            year: end.getFullYear(),
                            level: program.studyProgram.level.name,
                            program: program.studyProgram.name,
                        }).toString();
                    } else {
                        programString = this.$t(
                            'profile.personalDetails.studyProgramLevelInProgram',
                            {
                                level: program.studyProgram.level.name,
                                program: program.studyProgram.name,
                            }
                        ).toString();
                    }
                }

                experience.schoolId = program.studyProgram.schoolId || -1;
                experience.school =
                    (!!program.studyProgram.school
                        ? program.studyProgram.school.name
                        : program.studyProgram.customSchoolName) || '';
                experience.program = programString;
            }

            // Add years of work experience
            const numberOfYears = this.calculateTotalYearsOfWorkExperience;
            if (numberOfYears > 0) {
                experience.workExperience = this.$t('profile.personalDetails.numberOfYears', {
                    years: numberOfYears,
                }).toString();
            }

            return Object.keys(experience).length > 0 ? (experience as Experience) : null;
        },
        badges(): AttributeIconType[] {
            const icons: Record<AttributeIconType, boolean> = {
                job: !!this.candidate.preferences.workPreferences.length,
                flex: !['onsite', null].includes(this.candidate.preferences.eligibleWorkLocation),
                relo: !!this.candidate.preferences.eligibleForRelocation,
                lgb: !!this.candidate.preferences.memberOfLGBTQCommunity,
                eth: this.candidate.ethnicMinority === 'yes',
                pwd: this.candidate.disability === 'yes',
            };
            return Object.keys(icons).filter(
                key => icons[key as keyof typeof icons]
            ) as AttributeIconType[];
        },
        beDiscoveredHoverText(): string {
            return this.discover
                ? this.$t('profile.personalDetails.beDiscoveredModeTooltipActive').toString()
                : this.$t('profile.personalDetails.beDiscoveredModeTooltipInactive').toString();
        },
        calculateTotalYearsOfWorkExperience(): number {
            const experiences = this.candidate.workExperiences || [];
            let totalYears = 0;
            const now = new Date();

            for (let i = 0; i < experiences.length; i++) {
                const curWE = experiences[i];
                const startDate = curWE.start ? curWE.start : now;
                const endDate = curWE.end ? curWE.end : now;
                if (endDate.getTime() > now.getTime()) endDate.setTime(now.getTime());

                let currentDuration = endDate.getTime() - startDate.getTime();

                // Check for overlapping work experiences
                for (let j = i + 1; j < experiences.length; j++) {
                    const otherExperience = experiences[j];
                    const otherStartDate = otherExperience.start ? otherExperience.start : now;
                    const otherEndDate = otherExperience.end ? otherExperience.end : now;
                    if (otherEndDate.getTime() > now.getTime()) otherEndDate.setTime(now.getTime());

                    const overlapStart = Math.max(startDate.getTime(), otherStartDate.getTime());
                    const overlapEnd = Math.min(endDate.getTime(), otherEndDate.getTime());
                    const overlapDuration = Math.max(0, overlapEnd - overlapStart);

                    // If there is an overlap, subtract the overlap duration from the current duration
                    if (overlapDuration > 0) {
                        currentDuration -= overlapDuration;
                    }
                }
                totalYears += currentDuration / (1000 * 60 * 60 * 24 * 365);
            }
            return Math.round(totalYears);
        },
        studyPrograms(): CandidateStudyProgram[] {
            return this.$store.getters['profileModule/getStudyPrograms']();
        },
        topLevelStudyProgram(): CandidateStudyProgram | null {
            const studyPrograms = this.studyPrograms.filter(x => x.studyProgram.school?.id);

            const verified = studyPrograms.find(
                x => x.verification?.state === SchoolVerificationState.verified
            );
            if (verified) return verified;

            const pending = studyPrograms.find(
                x => x.verification?.state === SchoolVerificationState.pending
            );
            if (pending) return pending;

            const needVerification = studyPrograms.find(
                x =>
                    !x.verification ||
                    x.verification?.state === SchoolVerificationState.needVerification
            );
            if (needVerification) return needVerification;

            return null;
        },
        lastProfileImport(): ProfileImportResult | null {
            return this.$store.getters['profileModule/getLastProfileImport']();
        },
    },
    methods: {
        openProfileImportModal(state: ActionItem) {
            const event = new CustomEvent('profileimport:modal:show', {
                detail: state.id === 'latestImportBtn' ? 'Results' : 'EnterId',
            });
            window.dispatchEvent(event);
        },
        getLatestStudyProgram(): CandidateStudyProgram | null {
            const { studyPrograms } = this.candidate;
            if (!studyPrograms || !studyPrograms.length) return null;

            const cmp = new DateComparerBuilder<CandidateStudyProgram>().buildComparer(
                sp => sp.end,
                false,
                true
            ).compare;
            const programs = Object.assign([], studyPrograms).sort(cmp);
            return programs[0];
        },
        async loadSchoolLogo() {
            const program = this.getLatestStudyProgram();
            if (!program || !program.studyProgram.schoolId) return;

            const company = await CompanyService.getCompany(program.studyProgram.schoolId);
            if (!company.data.logo) return;

            this.schoolLogo = company.data.logo;
        },

        handleDiscover() {
            if (!this.discover) {
                // Check if the profile is complete to enable "be discovered" mode
                const scorer = new CandidateProfileScorer(
                    this.candidate,
                    this.user,
                    this.workAuthorizations
                );
                const score = scorer.getBeDiscoveredScore();
                if (score.status === CandidateProfileStatus.INCOMPLETE) {
                    DialogService.openMyProfileBeDiscoveredDialog(scorer);
                    return;
                }
            }

            const profileVisibility = this.discover ? 'private' : 'public_to_recruiters';
            this.isDiscoverLoading = true;

            this.service
                .updateCandidateProfile({
                    userId: this.user.id,
                    candidate: {
                        op: 'update',
                        value: { profileVisibility },
                    },
                })
                .finally(() => {
                    this.isDiscoverLoading = false;
                });
        },
        handleGenerateCV() {
            console.warn('Not implemented yet');
        },
        handleOpenPersonalDetailsModalEvent() {
            this.showModal = true;
        },
        updateKebabMenu() {
            var menuItems = [
                { id: importBtnId, title: String(this.$t('profile.import.kebabMenu.importBtn')) },
                {
                    id: latestImportBtnId,
                    title: String(this.$t('profile.import.kebabMenu.latestImportBtn')),
                },
            ] as ActionItem[];

            if (!this.lastProfileImport)
                menuItems = menuItems.filter(x => x.id !== latestImportBtnId) as ActionItem[];

            this.menuItems = menuItems;
        },
    },
    mounted() {
        this.loadSchoolLogo();

        this.updateKebabMenu();

        window.addEventListener(
            'candidate:profile:details',
            this.handleOpenPersonalDetailsModalEvent
        );
    },
    beforeDestroy() {
        window.removeEventListener(
            'candidate:profile:details',
            this.handleOpenPersonalDetailsModalEvent
        );
    },
    watch: {
        candidate(value: Candidate) {
            this.discover = value.profileVisibility === 'public_to_recruiters';
        },
        lastProfileImport() {
            this.updateKebabMenu();
        },
    },
});
